import Calendar from '@assets/icons/calendar.svg'
import Tick from '@assets/icons/tick.svg'
import Warning from '@assets/icons/warning.svg'
import { useBrand } from '@contexts/useBrand'
import { TIMES } from '@utils/constants'
import { customEvent, tracking } from '@utils/helpers/tracking'
import add from 'date-fns/add'
import format from 'date-fns/format'
import type { ReactNode } from 'react'
import DatePicker from 'react-datepicker'
import { Controller, useFormContext } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'

import SelectInput from './select'
import SwitchInput from './switch'

type Props = {
  name: string
  label: string
  helper?: ReactNode
  showAmPm?: boolean
  showTime?: boolean
  availability?: string
  optional?: boolean
  uppercase?: boolean
}

const DatepickerInput = ({
  name,
  label,
  helper,
  showAmPm,
  showTime,
  availability,
  optional,
  uppercase,
  ...rest
}: Props) => {
  const { control, watch, setValue, resetField, getFieldState, formState } =
    useFormContext()
  const brand = useBrand()
  const { error, isTouched, isDirty } = getFieldState(
    `${name}_input`,
    formState,
  )
  const watchDatepicker = watch(`${name}_input`, '')

  const handleClear = () => {
    setValue(name, '')
    setValue(`${name}_input`, '')
    resetField(`${name}_input`)
  }

  const getBrandClass = () => {
    switch (brand) {
      case 'kinto':
        return 'kinto-forms'
      case 'lexus':
        return 'lexus-forms'
      default:
        return ''
    }
  }

  return (
    <div
      className={twMerge(
        'grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 md:gap-4 items-start justify-top mb-4 md:mb-2 text-base-text',
        getBrandClass(),
      )}
      data-gt-name="componentevent"
    >
      <div className="md:col-span-2 lg:col-span-1">
        <label className="block" htmlFor={name}>
          <span className="inline-block mb-1 text-base-text">
            {label}{' '}
            {optional && (
              <span className="text-xs lowercase whitespace-nowrap">
                (optional)
              </span>
            )}
          </span>
        </label>
        <div className="relative grid gap-4 grid-cols-6 md:grid-cols-4 lg:grid-cols-6">
          <div
            className={
              showTime
                ? `relative col-span-3 md:col-span-2 lg:col-span-4`
                : `relative col-span-6 md:col-span-4 lg:col-span-6`
            }
          >
            <Controller
              control={control}
              name={`${name}_input`}
              render={({ field }) => (
                <DatePicker
                  filterDate={availability}
                  wrapperClassName={'forms-datepicker'}
                  className={twMerge(
                    `rounded-input border-input-border block w-full p-[0.7rem] pr-12`,
                    error && 'border-red',
                    !error && isTouched && isDirty && 'border-green',
                    uppercase && 'uppercase',
                  )}
                  aria-invalid={error ? 'true' : 'false'}
                  placeholderText="Select date"
                  onChange={(date) => {
                    if (date) {
                      field.onChange(date)
                      setValue(name, format(date, 'dd/MM/yyyy'))
                    }
                  }}
                  onBlur={(date) => {
                    field.onBlur(date)
                  }}
                  onCalendarOpen={() => {
                    customEvent({
                      name: 'componentevent',
                      // componentName needs updating to include form name
                      componentName: tracking.componentName(),
                      componentGroup: tracking.componentGroup(),
                      action: 'datepicker-input-interaction',
                      label: 'date',
                    })
                  }}
                  selected={field.value}
                  dateFormat="dd/MM/yyyy"
                  minDate={add(new Date(), { days: 1 })}
                  maxDate={add(new Date(), { days: 90 })}
                  showPopperArrow={false}
                  autocomplete={'off'}
                  {...rest}
                />
              )}
            />
            {watchDatepicker && (
              <button
                onClick={handleClear}
                className="absolute inset-y-0 right-16 top-3 h-[24px] z-9 text-base cursor-pointer hover:underline"
                data-gt-componentgroup={tracking.componentGroup()}
                data-gt-componentname={tracking.componentName()}
                data-gt-name="componentevent"
                data-gt-action="datepicker-click-cta"
                data-gt-label="clear"
              >
                <span className="text-tiny">clear</span>
              </button>
            )}
            <Calendar className="kinto-forms absolute inset-y-0 right-4 top-4 h-4 pointer-events-none text-base-text cursor-pointer" />
            {error && (
              <Warning className="absolute inset-y-0 right-10 top-4 h-4 pointer-events-none text-red" />
            )}
            {!error && isTouched && isDirty && (
              <Tick className="absolute inset-y-0 right-10 top-4 h-4 pointer-events-none text-green" />
            )}
          </div>
          {showAmPm && (
            <div className="h-full col-span-2 md:col-span-1 lg:col-span-2">
              <SwitchInput name={'time'} />
            </div>
          )}
          {showTime && (
            <div className="col-span-3 md:col-span-2 lg:col-span-2">
              <SelectInput
                options={TIMES}
                optionsLabel="Select time"
                isChild
                name={'time'}
              />
            </div>
          )}
        </div>
        {error && (
          <p className="text-red text-tiny mt-1" role="alert">
            {error.message}
          </p>
        )}
      </div>
      <div className="mt-1 md:mt-8 flex items-center md:min-h-[50px]">
        {helper && (
          <p
            className="text-tiny leading-5"
            dangerouslySetInnerHTML={{ __html: helper }}
          />
        )}
      </div>
    </div>
  )
}

export default DatepickerInput
